import { useEffect, useState } from "react";
import type {
  ActivateCmmdProjectLeadAuthorityResult,
  CmmdProjectLeadDetail,
  CmmdProjectLeadBriefing,
  CmmdProjectLeadEvents,
  CmmdProjectLeadExplanation,
  CmmdProjectLeadPlan,
  CmmdProjectLeadReadiness,
} from "@cmmd-forge/contracts";
import { cmmdApiClient } from "../../environments/cmmdApi";

export interface ProjectLeadSheetData {
  readonly detail: CmmdProjectLeadDetail;
  readonly briefing: CmmdProjectLeadBriefing;
  readonly plan: CmmdProjectLeadPlan;
  readonly readiness: CmmdProjectLeadReadiness;
  readonly explanation: CmmdProjectLeadExplanation;
  readonly events: CmmdProjectLeadEvents;
}

export type ProjectLeadActivationState =
  | { readonly kind: "idle" }
  | { readonly kind: "activating" }
  | { readonly kind: "activated"; readonly result: ActivateCmmdProjectLeadAuthorityResult }
  | { readonly kind: "error"; readonly message: string };

export interface ProjectLeadSheetController {
  readonly data: ProjectLeadSheetData | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly reconciling: boolean;
  readonly actionError: string | null;
  readonly activation: ProjectLeadActivationState;
  readonly decideApproval: (
    approvalId: string,
    decision: "approved" | "denied",
    reason: string,
  ) => Promise<void>;
  readonly activate: () => void;
  readonly reload: () => void;
  readonly reconcile: () => void;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Project Lead details are unavailable.";
}

async function loadProjectLeadSheetData(projectId: string): Promise<ProjectLeadSheetData> {
  const [detail, briefing, plan, readiness, explanation, events] = await Promise.all([
    cmmdApiClient.getProjectLeadDetail(projectId),
    cmmdApiClient.getProjectLeadBriefing(projectId),
    cmmdApiClient.getProjectLeadPlan(projectId),
    cmmdApiClient.getProjectLeadReadiness(projectId),
    cmmdApiClient.explainProjectLead(projectId),
    cmmdApiClient.listProjectLeadEvents(projectId, { limit: 50 }),
  ]);
  return { detail, briefing, plan, readiness, explanation, events };
}

export function useProjectLeadSheet(
  projectId: string,
  enabled: boolean,
): ProjectLeadSheetController {
  const [data, setData] = useState<ProjectLeadSheetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [reconciling, setReconciling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activation, setActivation] = useState<ProjectLeadActivationState>({ kind: "idle" });

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setLoading(true);
    setError(null);
    void loadProjectLeadSheetData(projectId)
      .then((nextData) => {
        if (active) setData(nextData);
      })
      .catch((cause: unknown) => {
        if (active) setError(errorMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [enabled, projectId, reloadVersion]);

  const reconcile = async () => {
    setReconciling(true);
    setActionError(null);
    try {
      await cmmdApiClient.reconcileProjectLead(projectId, { batchSize: 50 });
      setReloadVersion((version) => version + 1);
    } catch (cause) {
      setActionError(errorMessage(cause));
    } finally {
      setReconciling(false);
    }
  };

  const activate = async () => {
    setActivation({ kind: "activating" });
    try {
      const result = await cmmdApiClient.activateProjectLeadAutomaticStaffing(projectId);
      setActivation({ kind: "activated", result });
      setReloadVersion((version) => version + 1);
    } catch (cause) {
      setActivation({ kind: "error", message: errorMessage(cause) });
    }
  };

  const decideApproval = async (
    approvalId: string,
    decision: "approved" | "denied",
    reason: string,
  ) => {
    setActionError(null);
    try {
      await cmmdApiClient.decideProjectLeadApproval(projectId, approvalId, {
        decision,
        reason,
        idempotencyKey: `project-lead-approval:${approvalId}:${crypto.randomUUID()}`,
      });
      setReloadVersion((version) => version + 1);
    } catch (cause) {
      setActionError(errorMessage(cause));
      throw cause;
    }
  };

  return {
    data,
    loading,
    error,
    reconciling,
    actionError,
    activation,
    decideApproval,
    activate: () => void activate(),
    reload: () => setReloadVersion((version) => version + 1),
    reconcile: () => void reconcile(),
  };
}
